import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import './Pricing.css';

const tiers = [
  {
    name: 'Starter',
    price: '₹1,999/month',
    runs: 'Up to 5,000 runs/month',
    blurb: 'One agent, shipped fast.',
    features: ['1 agent (WhatsApp / Telegram / web)', 'Up to 3 integrations', '2 weeks delivery', '30 days post-launch support', 'Basic analytics'],
    cta: 'Get Started',
    to: '/contact',
    featured: false,
  },
  {
    name: 'Growth',
    price: '₹4,999/month',
    runs: 'Up to 25,000 runs/month',
    blurb: 'Multi-channel, with automation.',
    features: ['2–4 agents (multi-channel)', 'Unlimited integrations', 'Workflow automation included', '4–6 week delivery', '90 days support', 'Full analytics dashboard'],
    cta: 'Book a Call',
    to: '/contact',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    runs: 'Unlimited runs',
    blurb: 'Fully bespoke, SLA-backed.',
    features: ['Fully custom scope', 'Dedicated team', 'On-premise / private cloud', 'Compliance & security review', 'SLA-backed support', 'Ongoing retainer available'],
    cta: 'Contact Us',
    to: '/contact',
    featured: false,
  },
];

const faqs = [
  ['How long does a custom agent take to build?', 'Most starter builds ship in about 2 weeks; multi-agent and enterprise builds run 4–6 weeks depending on scope and integrations.'],
  ['What platforms do you deploy to?', 'WhatsApp Business, Telegram, web chat widgets, Slack, and any system with an API. We deploy to your channels and integrate with your stack.'],
  ['Can you integrate with our existing CRM / ERP?', 'Yes. We connect to HubSpot, Salesforce, internal databases, ERPs, and custom APIs. If it has an interface, the agent can use it.'],
  ['Do you support on-premise deployments?', 'Yes — for enterprise builds we offer on-premise and private-cloud deployment for data residency and compliance.'],
  ['What LLM models do you use?', 'We’re model-agnostic: OpenAI, Claude, Gemini, Llama, or your own fine-tuned model. We choose based on your needs, budget, and privacy requirements.'],
  ['Can we own the code and infrastructure?', 'Yes. Ownership terms are part of scoping — many enterprise clients own their code and run on their own infrastructure.'],
  ['Do you offer ongoing maintenance?', 'Yes. Every build includes post-launch support, and we offer retainers for ongoing iteration, monitoring, and maintenance.'],
];

const FaqItem = ({ q, a, reduced }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{q}</span>
        <ChevronDown size={18} className="faq-chevron" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="faq-a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reduced ? 0.15 : 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <p>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Pricing = () => {
  const reduced = useReducedMotion();

  const fade = {
    hidden: { opacity: 0, y: reduced ? 0 : 22 },
    visible: (i = 0) => ({
      opacity: 1, y: 0,
      transition: { duration: reduced ? 0.2 : 0.6, delay: reduced ? 0 : i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <div className="pricing-hero-glow" aria-hidden="true" />
        <motion.h1 className="pricing-title" initial="hidden" animate="visible" variants={fade}>
          Transparent pricing for every stage.
        </motion.h1>
        <motion.p className="pricing-sub" initial="hidden" animate="visible" variants={fade} custom={1}>
          Whether you’re an SMB or an enterprise, there’s an engagement model that works.
        </motion.p>
      </section>

      <section className="pricing-tiers">
        {tiers.map((t, i) => (
          <motion.div
            key={t.name}
            className={`tier ${t.featured ? 'featured' : ''}`}
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={fade} custom={i}
          >
            {t.featured && <span className="tier-badge">Most popular</span>}
            <h2 className="tier-name">{t.name}</h2>
            <div className="tier-price">{t.price}</div>
            <div className="tier-runs">{t.runs}</div>
            <p className="tier-blurb">{t.blurb}</p>
            <ul className="tier-features">
              {t.features.map((f) => <li key={f}><Check size={15} /> {f}</li>)}
            </ul>
            <Link to={t.to} className={t.featured ? 'tier-cta primary' : 'tier-cta'}>
              {t.cta} <ArrowRight size={16} />
            </Link>
          </motion.div>
        ))}
      </section>

      <div className="pricing-build-note">
        <span className="pricing-build-note-icon">🛠</span>
        <p>
          <strong>Building cost varies by complexity.</strong> The monthly plans above cover agent runtime and usage.
          The one-time build fee depends on the number of agents, integrations, and custom logic required — we scope this transparently on your first call.
        </p>
        <Link to="/contact" className="pricing-build-note-link">Get a scoping estimate <ArrowRight size={14} /></Link>
      </div>

      {/* Agent Studio tier */}
      <section className="pricing-studio">
        <div className="pricing-studio-card">
          <div>
            <span className="pricing-studio-chip">Coming Soon</span>
            <h3>Agent Studio</h3>
            <p>Self-serve, subscription pricing with a free tier planned. Build and deploy agents yourself.</p>
          </div>
          <Link to="/agent-studio" className="btn-secondary">Join Waitlist <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-faq">
        <h2 className="pricing-faq-title">Frequently asked</h2>
        <div className="faq-list">
          {faqs.map(([q, a]) => <FaqItem key={q} q={q} a={a} reduced={reduced} />)}
        </div>
      </section>
    </div>
  );
};
